const CHUNK_RECOVERY_KEY = "app_chunk_recovery_attempt";
const CHUNK_RECOVERY_TTL = 60 * 1000;

const chunkLoadErrorPatterns = [
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  "ChunkLoadError",
  "Loading chunk",
];

export function getErrorText(error: unknown) {
  if (error instanceof Error) {
    return [error.name, error.message, error.stack].filter(Boolean).join("\n");
  }

  return String(error);
}

export function isStaleChunkError(error: unknown) {
  const errorText = getErrorText(error);

  return chunkLoadErrorPatterns.some((pattern) => errorText.includes(pattern));
}

export function recoverFromStaleChunk(error: unknown) {
  if (typeof window === "undefined" || !isStaleChunkError(error)) {
    return false;
  }

  const recoveryKey = getRecoveryUrlKey();
  const previousAttempt = window.sessionStorage.getItem(CHUNK_RECOVERY_KEY);

  if (previousAttempt) {
    try {
      const { key, time } = JSON.parse(previousAttempt) as {
        key?: string;
        time?: number;
      };

      if (
        key === recoveryKey &&
        typeof time === "number" &&
        Date.now() - time < CHUNK_RECOVERY_TTL
      ) {
        return false;
      }
    } catch {
      window.sessionStorage.removeItem(CHUNK_RECOVERY_KEY);
    }
  }

  window.sessionStorage.setItem(
    CHUNK_RECOVERY_KEY,
    JSON.stringify({ key: recoveryKey, time: Date.now() }),
  );

  void refreshServiceWorkers().finally(() => {
    const recoveryUrl = new URL(window.location.href);
    recoveryUrl.searchParams.set("recovered_from_chunk", Date.now().toString());
    window.location.replace(recoveryUrl.toString());
  });

  return true;
}

function getRecoveryUrlKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete("recovered_from_error");
  url.searchParams.delete("recovered_from_chunk");
  url.searchParams.delete("pwa_updated_at");

  return `${url.pathname}${url.search}${url.hash}`;
}

async function refreshServiceWorkers() {
  if (!("serviceWorker" in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();

  await Promise.allSettled(
    registrations.map(async (registration) => {
      await registration.update();
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
    }),
  );
}

